import React, { useContext, useEffect, useRef, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Lock, EyeOff, Eye } from "lucide-react";

import { useTranslation } from "react-i18next";
import AuthContext from "../../store/auth-context";
import Toast from "../../common/Toast/Toast";
import { POST } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import {
  automationTestIds,
  singletonFormIds,
} from "../../testing/automationTestIds";
import { pageRootTestIds } from "../../testing/pageRootTestIds";
import AuthBrandHeader from "../../components/AuthBrandHeader/AuthBrandHeader";
import { getProfilePhotoDisplaySrc } from "../../utils/profilePhoto";

const RESET_PASSWORD_GUARD_KEY = "reset_password_in_progress";

const ResetPassword = () => {
  const { t } = useTranslation();
  const authContext = useContext(AuthContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const hasInitiatedLogoutRef = useRef(false);
  const isLoggedInRef = useRef(authContext.isLoggedIn);
  const logoutRef = useRef(authContext.logout);
  const normalizeText = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const fullName = [
    authContext?.userDetails?.first_name,
    authContext?.userDetails?.last_name,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  const accountFallback =
    authContext?.userDetails?.name ||
    authContext?.userDetails?.username ||
    authContext?.userDetails?.user_name ||
    authContext?.userDetails?.email ||
    "";

  const accountName = fullName || accountFallback;

  const accountPhotoSrc = getProfilePhotoDisplaySrc(
    authContext?.userDetails?.profile_photo,
    authContext?.profilePhotoCache,
  );

  const displayName =
    authContext?.userDetails?.first_name ||
    fullName ||
    (accountFallback.includes("@")
      ? accountFallback.split("@")[0]
      : accountFallback);

  const isFirstTimeLogin =
    authContext?.userDetails?.is_first_time_login === true;

  const userIdentifiers = [
    authContext?.userDetails?.username,
    authContext?.userDetails?.user_name,
    authContext?.userDetails?.name,
    authContext?.userDetails?.email,
  ]
    .filter(Boolean)
    .map((item) => normalizeText(item))
    .filter((item) => item.length >= 3);

  const validationSchema = Yup.object({
    old_password: Yup.string().required(
      t("resetpassword:validation.current_password_required"),
    ),
    new_password: Yup.string()
      .required(t("resetpassword:validation.new_password_required"))
      .min(8, t("resetpassword:validation.new_password_min_length"))
      .test(
        "not-similar-to-identity",
        t("resetpassword:validation.new_password_not_similar"),
        (value) => {
          if (!value || userIdentifiers.length === 0) return true;
          const passwordText = normalizeText(value);
          return !userIdentifiers.some((identifier) =>
            passwordText.includes(identifier),
          );
        },
      )
      .test(
        "not-numeric-only",
        t("resetpassword:validation.new_password_not_numeric_only"),
        (value) => !/^\d+$/.test(value || ""),
      )
      .test(
        "allowed-characters-only",
        t("resetpassword:validation.new_password_allowed_characters"),
        (value) => /^[A-Za-z\d@$!%*?&]+$/.test(value || ""),
      )
      .test(
        "character-mix",
        t("resetpassword:validation.new_password_mixed_characters"),
        (value) =>
          /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/.test(
            value || "",
          ),
      )
      .test(
        "no-3-identical-in-a-row",
        t("resetpassword:validation.new_password_no_repeat"),
        (value) => !/(.)\1{2,}/.test(value || ""),
      )
      .test(
        "no-3-digit-sequences",
        t("resetpassword:validation.new_password_no_sequence"),
        (value) => !/(012|123|234|345|456|567|678|789)/.test(value || ""),
      ),
    re_enter_password: Yup.string()
      .required(t("resetpassword:validation.confirm_password_required"))
      .oneOf(
        [Yup.ref("new_password")],
        t("resetpassword:validation.confirm_password_mismatch"),
      ),
  });

  const handleResetPassword = async (values) => {
    let passwordChanged = false;
    try {
      setIsSubmitting(true);

      const response = await POST(EntityConstants.CHANGE_PASSWORD, values);
      const data = await response.json();

      if (response.ok && !data?.error) {
        passwordChanged = true;
        Toast({
          type: "success",
          title: t("resetpassword:success_title"),
          message: data?.message || t("resetpassword:success_message"),
        });

        setTimeout(() => {
          hasInitiatedLogoutRef.current = true;
          authContext.logout("password_updated", {
            email: authContext?.userDetails?.email || "",
          });
        }, 700);
        return;
      }
      Toast({
        type: "error",
        title: t("resetpassword:error_title"),
        message:
          data?.message || data?.error || t("resetpassword:error_message"),
      });
    } catch (error) {
      Toast({
        type: "error",
        title: t("resetpassword:error_title"),
        message: error?.message || t("resetpassword:error_message"),
      });
    } finally {
      if (!passwordChanged) {
        setIsSubmitting(false);
      }
    }
  };

  useEffect(() => {
    localStorage.setItem(RESET_PASSWORD_GUARD_KEY, "true");
  }, []);

  useEffect(() => {
    isLoggedInRef.current = authContext.isLoggedIn;
    logoutRef.current = authContext.logout;
  }, [authContext.isLoggedIn, authContext.logout]);

  useEffect(() => {
    return () => {
      if (hasInitiatedLogoutRef.current || !isLoggedInRef.current) {
        return;
      }

      hasInitiatedLogoutRef.current = true;
      logoutRef.current();
    };
  }, []);

  return (
    <div
      className="w-full min-h-screen bg-cfSecondary flex flex-col"
      id={pageRootTestIds.resetPassword}
    >
      <AuthBrandHeader
        accountName={accountName}
        accountPhotoSrc={accountPhotoSrc}
      />
      <div className="w-full max-w-lg m-auto px-4 py-8">
        <div className="flex flex-col items-center text-center mb-7">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-cfDisabledLight rounded-full blur-xl"></div>
              <div className="relative p-[1.5rem] rounded-full bg-cfDisabledLight border-[0.1rem] border-cfDisabled">
                <Lock className="w-7 h-7 text-cfPrimary" />
              </div>
            </div>
          </div>
          {isFirstTimeLogin && (
            <span
              id={automationTestIds.resetPasswordFirstTimeNote}
              className="mb-3 inline-flex items-center rounded-full bg-cfDisabledLight px-3 py-1 text-xs font-semibold uppercase tracking-wide text-cfPrimary"
            >
              {t("resetpassword:first_time_badge")}
            </span>
          )}
          <h1 className="text-3xl font-bold text-foreground mb-3 text-balance">
            {isFirstTimeLogin && displayName
              ? t("resetpassword:first_time_title", { name: displayName })
              : t("resetpassword:title")}
          </h1>
          <p className="text-cfSignInSignUpIntroMessageText text-base">
            {isFirstTimeLogin
              ? t("resetpassword:first_time_subtitle")
              : t("resetpassword:subtitle")}
          </p>
        </div>

        <div className="bg-card border-2 border-[#b1b7f2] rounded-2xl shadow-lg backdrop-blur-sm overflow-hidden ">
          <Formik
            initialValues={{
              old_password: "",
              new_password: "",
              re_enter_password: "",
            }}
            validationSchema={validationSchema}
            onSubmit={handleResetPassword}
          >
            {() => (
              <Form id={singletonFormIds.resetPasswordForm}>
                <div className="flex flex-col">
                  <div className="p-6 md:p-8 md:border-r md:border-border ">
                    <div>
                      <label
                        htmlFor={automationTestIds.resetPasswordCurrent}
                        className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText after:content-['*'] after:text-red-500"
                      >
                        {t("resetpassword:current_password_label")}
                      </label>
                      <div className="mt-3 flex items-center justify-between rounded-md w-full h-11 mr-[.14rem] pl-2 border border-cfSignInSignUpInputBorder">
                        <Field
                          type={showCurrentPassword ? "text" : "password"}
                          name="old_password"
                          id={automationTestIds.resetPasswordCurrent}
                          placeholder={t(
                            "resetpassword:current_password_placeholder",
                          )}
                          className="focus:outline-none text-base text-cfSignInSignUpInputPlaceholderText border-none w-full bg-transparent"
                        />
                        <button
                          type="button"
                          id={automationTestIds.resetPasswordToggleCurrent}
                          className="pl-1 pr-2 text-cfSignInSignUpInputPlaceholderText focus:outline-none"
                          aria-label={
                            showCurrentPassword
                              ? t("resetpassword:hide_password")
                              : t("resetpassword:show_password")
                          }
                          onClick={() =>
                            setShowCurrentPassword((prev) => !prev)
                          }
                        >
                          {showCurrentPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground ">
                        {t("resetpassword:current_password_helper")}
                      </p>
                      <ErrorMessage
                        name="old_password"
                        component="p"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <div className="space-y-3 mt-4">
                      <label
                        htmlFor={automationTestIds.resetPasswordNew}
                        className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText after:content-['*'] after:text-red-500"
                      >
                        {t("resetpassword:new_password_label")}
                      </label>
                      <div className="flex items-center justify-between rounded-md w-full h-11 mr-[.14rem] pl-2 border border-cfSignInSignUpInputBorder">
                        <Field
                          type={showNewPassword ? "text" : "password"}
                          name="new_password"
                          id={automationTestIds.resetPasswordNew}
                          placeholder={t(
                            "resetpassword:new_password_placeholder",
                          )}
                          className="focus:outline-none text-base text-cfSignInSignUpInputPlaceholderText border-none w-full bg-transparent"
                        />
                        <button
                          type="button"
                          id={automationTestIds.resetPasswordToggleNew}
                          className="pl-1 pr-2 text-cfSignInSignUpInputPlaceholderText focus:outline-none"
                          aria-label={
                            showNewPassword
                              ? t("resetpassword:hide_password")
                              : t("resetpassword:show_password")
                          }
                          onClick={() => setShowNewPassword((prev) => !prev)}
                        >
                          {showNewPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                      <ErrorMessage
                        name="new_password"
                        component="p"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <div className="space-y-3 mt-4">
                      <label
                        htmlFor={automationTestIds.resetPasswordConfirm}
                        className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText after:content-['*'] after:text-red-500"
                      >
                        {t("resetpassword:confirm_password_label")}
                      </label>
                      <div className="flex items-center justify-between rounded-md w-full h-11 mr-[.14rem] pl-2 border border-cfSignInSignUpInputBorder">
                        <Field
                          type={showConfirmPassword ? "text" : "password"}
                          name="re_enter_password"
                          id={automationTestIds.resetPasswordConfirm}
                          placeholder={t(
                            "resetpassword:confirm_password_placeholder",
                          )}
                          className="focus:outline-none text-base text-cfSignInSignUpInputPlaceholderText border-none w-full bg-transparent"
                        />
                        <button
                          type="button"
                          id={automationTestIds.resetPasswordToggleConfirm}
                          className="pl-1 pr-2 text-cfSignInSignUpInputPlaceholderText focus:outline-none"
                          aria-label={
                            showConfirmPassword
                              ? t("resetpassword:hide_password")
                              : t("resetpassword:show_password")
                          }
                          onClick={() =>
                            setShowConfirmPassword((prev) => !prev)
                          }
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="w-5 h-5" />
                          ) : (
                            <Eye className="w-5 h-5" />
                          )}
                        </button>
                      </div>
                      <ErrorMessage
                        name="re_enter_password"
                        component="p"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-4 mt-6 w-full">
                      <button
                        type="submit"
                        id={automationTestIds.resetPasswordSubmit}
                        disabled={isSubmitting}
                        className="rounded-[8px] text-base font-semibold bg-cfButton hover:bg-cfPrimaryDark h-[3rem] px-2 text-cfSecondary focus:outline-none"
                      >
                        {isSubmitting
                          ? t("resetpassword:resetting_button")
                          : t("resetpassword:reset_button")}
                      </button>
                    </div>
                  </div>

                  <div className="p-6 md:p-8 bg-cfSearchContainer flex flex-col">
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <p className="text-md font-bold text-foreground">
                          {t("resetpassword:requirements_title")}
                        </p>
                      </div>
                      <ul className="list-disc pl-2 text-sm text-cfSignInSignUpInputPlaceholderText space-y-2 md:space-y-3">
                        <li>{t("resetpassword:requirement_min_length")}</li>

                        <li>
                          {t("resetpassword:requirement_mixed_characters")}
                        </li>
                        <li>
                          {t("resetpassword:requirement_allowed_characters")}
                        </li>
                        <li>{t("resetpassword:requirement_no_repeat")}</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
